import React, { useState } from 'react';
import { Menu, X, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackCTAClick } from '../utils/analytics';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  const handleGetStartedClick = () => {
    trackCTAClick('Get Started - Header', location.pathname);
  };

  const navLinks = [
    { label: 'Platform', href: '/home' },
    { label: 'Product', href: '/product' },
    { label: 'Features', href: '/features' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Industries', href: '/industries' },
    { label: 'Partners', href: '/partners' },
    { label: 'About', href: '/about' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0F1C]/90 backdrop-blur-lg border-b border-[#2563EB]/20">
      <div className="max-w-7xl mx-auto px-6 py-1">
        <div className="flex items-center justify-between">
          {/* Logo with AxioSera Tagline */}
          <div className="flex items-center gap-3">
            <Link to="/">
              <img src="/logo.png" alt="PriceStrike - AI Pricing Intelligence Platform" className="h-36 w-auto" />
            </Link>
            <a 
              href="https://axiosera.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden lg:block border-l border-[#2563EB]/30 pl-3"
              aria-label="Visit AxioSera website"
            >
              <span className="text-xs text-[#94A3B8] hover:text-[#2563EB] transition-colors cursor-pointer">
                An AxioSera Product
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navLinks.map((link, index) => (
              <Link
                key={index}
                to={link.href}
                className={`text-lg font-medium transition-colors duration-200 ${
                  isActive(link.href)
                    ? 'text-[#2563EB]'
                    : 'text-[#94A3B8] hover:text-[#F8FAFC]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-transparent flex items-center gap-2">
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  onClick={logout}
                  className="text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-transparent"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-transparent">
                    Login
                  </Button>
                </Link>
                <Link to="/coming-soon" onClick={handleGetStartedClick}>
                  <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-6 py-2 rounded-lg transition-all duration-300">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#F8FAFC]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-[#2563EB]/20 pt-4">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className={`text-lg font-medium transition-colors duration-200 ${
                    isActive(link.href)
                      ? 'text-[#2563EB]'
                      : 'text-[#94A3B8] hover:text-[#F8FAFC]'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 mt-4">
                {isAuthenticated ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="text-[#94A3B8] hover:text-[#F8FAFC] justify-start w-full flex items-center gap-2">
                        <LayoutDashboard className="w-4 h-4" />
                        Dashboard
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="text-[#94A3B8] hover:text-[#F8FAFC] justify-start w-full"
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="text-[#94A3B8] hover:text-[#F8FAFC] justify-start w-full">
                        Login
                      </Button>
                    </Link>
                    <Link to="/coming-soon" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white rounded-lg w-full">
                        Get Started
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;